import { z } from 'zod';
import { auditFieldsSchema, centsSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, } from './common.js';
/* ========================= Departamentos e cargos ======================== */
export const departmentSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    name: z.string(),
    description: z.string().nullable(),
    isActive: z.boolean(),
    employeeCount: z.number().int(),
});
export const departmentInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome do departamento').max(120),
    description: z.string().trim().max(500).optional().nullable(),
    isActive: z.boolean().default(true),
});
export const positionSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    name: z.string(),
    description: z.string().nullable(),
    departmentId: idSchema.nullable(),
    departmentName: z.string().nullable(),
    baseSalaryCents: z.number().int(),
    isActive: z.boolean(),
    employeeCount: z.number().int(),
});
export const positionInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome do cargo').max(120),
    description: z.string().trim().max(500).optional().nullable(),
    departmentId: idSchema.optional().nullable(),
    baseSalaryCents: centsSchema.min(0).default(0),
    isActive: z.boolean().default(true),
});
/* ============================== Colaboradores ============================ */
export const EMPLOYEE_STATUSES = ['ATIVO', 'FERIAS', 'AFASTADO', 'DESLIGADO'];
export const employeeStatusSchema = z.enum(EMPLOYEE_STATUSES);
export const EMPLOYEE_STATUS_LABELS = {
    ATIVO: 'Ativo',
    FERIAS: 'Em férias',
    AFASTADO: 'Afastado',
    DESLIGADO: 'Desligado',
};
export const employeeDocumentSchema = z.object({
    id: idSchema,
    type: z.string(),
    label: z.string(),
    number: z.string().nullable(),
    issuer: z.string().nullable(),
    issueDate: z.string().nullable(),
    expiryDate: z.string().nullable(),
    notes: z.string().nullable(),
    isActive: z.boolean(),
    /// Derivado da data: um documento vencido não pode depender de job diário.
    isExpired: z.boolean(),
    daysToExpiry: z.number().int().nullable(),
});
/**
 * Colaborador.
 *
 * Nome, CPF, telefone e endereço NÃO se repetem aqui — vivem na Pessoa. Este
 * registro guarda só o vínculo de trabalho, pela mesma razão do cadastro
 * unificado: um CPF, um cadastro.
 */
export const employeeSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    personId: idSchema,
    name: z.string(),
    document: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    registration: z.string(),
    positionId: idSchema.nullable(),
    positionName: z.string().nullable(),
    departmentId: idSchema.nullable(),
    departmentName: z.string().nullable(),
    unitId: idSchema.nullable(),
    hireDate: z.string(),
    dismissalDate: z.string().nullable(),
    dismissalReason: z.string().nullable(),
    salaryCents: z.number().int(),
    workloadHours: z.number().int(),
    status: employeeStatusSchema,
    notes: z.string().nullable(),
    documents: z.array(employeeDocumentSchema),
    /// Quantos documentos estão vencidos — alimenta a AttentionStrip.
    expiredDocumentCount: z.number().int(),
});
export const employeeInputSchema = z.object({
    personId: idSchema,
    registration: z.string().trim().min(1, 'Informe a matrícula').max(30).toUpperCase(),
    positionId: idSchema.optional().nullable(),
    departmentId: idSchema.optional().nullable(),
    unitId: idSchema.optional().nullable(),
    hireDate: isoDateSchema,
    salaryCents: centsSchema.min(0).default(0),
    workloadHours: z.number().int().min(1).max(60).default(44),
    notes: z.string().trim().max(1000).optional().nullable(),
});
export const employeeUpdateSchema = employeeInputSchema.omit({ personId: true }).partial();
export const dismissEmployeeSchema = z.object({
    dismissalDate: isoDateSchema,
    reason: z.string().trim().min(5, 'Informe o motivo do desligamento').max(500),
});
export const employeeDocumentInputSchema = z.object({
    type: z.string().trim().min(2, 'Informe o tipo').max(60),
    label: z.string().trim().min(2, 'Descreva o documento').max(120),
    number: z.string().trim().max(60).optional().nullable(),
    issuer: z.string().trim().max(120).optional().nullable(),
    issueDate: isoDateSchema.optional().nullable(),
    expiryDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(500).optional().nullable(),
});
export const employeeListQuerySchema = paginationQuerySchema.extend({
    status: employeeStatusSchema.optional(),
    departmentId: idSchema.optional(),
    positionId: idSchema.optional(),
    expiredDocuments: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const employeeListResponseSchema = paginated(employeeSchema);
/* ========================== Férias e afastamentos ======================== */
export const LEAVE_TYPES = [
    'FERIAS',
    'ATESTADO',
    'LICENCA_MATERNIDADE',
    'LICENCA_PATERNIDADE',
    'NAO_REMUNERADA',
    'SUSPENSAO',
    'OUTRO',
];
export const leaveTypeSchema = z.enum(LEAVE_TYPES);
export const LEAVE_TYPE_LABELS = {
    FERIAS: 'Férias',
    ATESTADO: 'Atestado médico',
    LICENCA_MATERNIDADE: 'Licença-maternidade',
    LICENCA_PATERNIDADE: 'Licença-paternidade',
    NAO_REMUNERADA: 'Licença não remunerada',
    SUSPENSAO: 'Suspensão',
    OUTRO: 'Outro',
};
export const LEAVE_STATUSES = ['PENDENTE', 'APROVADO', 'REPROVADO', 'CANCELADO'];
export const leaveStatusSchema = z.enum(LEAVE_STATUSES);
export const LEAVE_STATUS_LABELS = {
    PENDENTE: 'Aguardando aprovação',
    APROVADO: 'Aprovado',
    REPROVADO: 'Reprovado',
    CANCELADO: 'Cancelado',
};
export const leaveSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    employeeId: idSchema,
    employeeName: z.string().nullable(),
    registration: z.string().nullable(),
    type: leaveTypeSchema,
    startDate: z.string(),
    endDate: z.string(),
    totalDays: z.number().int(),
    reason: z.string().nullable(),
    status: leaveStatusSchema,
    approvedById: idSchema.nullable(),
    approvedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
});
export const leaveInputSchema = z
    .object({
    employeeId: idSchema,
    type: leaveTypeSchema,
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    reason: z.string().trim().max(500).optional().nullable(),
})
    .refine((v) => v.endDate >= v.startDate, {
    message: 'O fim não pode ser antes do início',
    path: ['endDate'],
});
export const leaveListQuerySchema = paginationQuerySchema.extend({
    status: leaveStatusSchema.optional(),
    type: leaveTypeSchema.optional(),
    employeeId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const leaveListResponseSchema = paginated(leaveSchema);
/* =========================== Folha de pagamento ========================== */
export const PAYROLL_STATUSES = ['RASCUNHO', 'APROVADA', 'PAGA', 'CANCELADA'];
export const payrollStatusSchema = z.enum(PAYROLL_STATUSES);
export const PAYROLL_STATUS_LABELS = {
    RASCUNHO: 'Rascunho',
    APROVADA: 'Aprovada',
    PAGA: 'Paga',
    CANCELADA: 'Cancelada',
};
export const payrollItemSchema = z.object({
    id: idSchema,
    employeeId: idSchema,
    employeeName: z.string().nullable(),
    registration: z.string().nullable(),
    positionName: z.string().nullable(),
    baseSalaryCents: z.number().int(),
    overtimeCents: z.number().int(),
    bonusesCents: z.number().int(),
    grossCents: z.number().int(),
    inssCents: z.number().int(),
    irrfCents: z.number().int(),
    otherDiscountsCents: z.number().int(),
    discountsCents: z.number().int(),
    netCents: z.number().int(),
    notes: z.string().nullable(),
});
export const payrollSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    referenceMonth: z.string(),
    status: payrollStatusSchema,
    costCenterId: idSchema,
    costCenterName: z.string().nullable(),
    chartAccountId: idSchema,
    chartAccountName: z.string().nullable(),
    totalGrossCents: z.number().int(),
    totalDiscountCents: z.number().int(),
    totalNetCents: z.number().int(),
    paymentDate: z.string().nullable(),
    approvedAt: z.string().nullable(),
    paidAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    notes: z.string().nullable(),
    items: z.array(payrollItemSchema),
    employeeCount: z.number().int(),
    /// Lançamento de contas a pagar gerado ao aprovar a folha.
    financialEntryId: idSchema.nullable(),
});
export const payrollInputSchema = z.object({
    /// Competência AAAA-MM.
    referenceMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use o formato AAAA-MM (ex.: 2026-08)'),
    costCenterId: idSchema,
    chartAccountId: idSchema,
    paymentDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(1000).optional().nullable(),
});
export const payrollItemInputSchema = z.object({
    items: z
        .array(z.object({
        employeeId: idSchema,
        baseSalaryCents: centsSchema.min(0),
        overtimeCents: centsSchema.min(0).default(0),
        bonusesCents: centsSchema.min(0).default(0),
        inssCents: centsSchema.min(0).default(0),
        irrfCents: centsSchema.min(0).default(0),
        otherDiscountsCents: centsSchema.min(0).default(0),
        notes: z.string().trim().max(300).optional().nullable(),
    }))
        .min(1, 'Informe ao menos um colaborador'),
});
export const approvePayrollSchema = z.object({
    /// Vencimento do lançamento gerado no contas a pagar.
    dueDate: isoDateSchema,
});
export const payrollListQuerySchema = paginationQuerySchema.extend({
    status: payrollStatusSchema.optional(),
});
export const payrollListResponseSchema = paginated(payrollSchema);
//# sourceMappingURL=rh.js.map